/**
 * Invitation accept page.
 *
 * Handles the /invitations/accept?token=... route that invitation emails link to.
 *
 * Flow:
 *  1. Extract token from URL.
 *  2. Fetch public preview (org name, role, email) — shown while loading.
 *  3. If not authenticated → redirect to /login?return_to=/invitations/accept?token=...
 *     so the full URL is preserved through the login flow.
 *  4. If authenticated → show preview and Accept button.
 *  5. On accept → POST to gateway, refresh auth, navigate to /settings.
 *  6. On error (expired, already used, not found) → show clear error state.
 */

import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildLoginUrl } from '../lib/kratos';
import { config } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

interface InvitationPreview {
  org_name: string;
  target_role: string;
  email: string;
  expires_at: string;
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'preview'; preview: InvitationPreview }
  | { phase: 'accepting' }
  | { phase: 'success'; orgName: string; role: string }
  | { phase: 'error'; message: string };

export default function InvitationAccept() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, refresh } = useAuth();

  const token = new URLSearchParams(location.search).get('token') ?? '';
  const [state, setState] = useState<PageState>({ phase: 'loading' });

  // Fetch the public preview (org name, role, invitee email)
  const fetchPreview = useCallback(async () => {
    if (!token) {
      setState({ phase: 'error', message: 'Invalid invitation link — no token found.' });
      return;
    }

    try {
      const resp = await fetch(
        `${config.gatewayUrl}/v1/invitations/${encodeURIComponent(token)}/preview`,
      );

      if (resp.status === 404) {
        setState({ phase: 'error', message: 'This invitation was not found. It may have already been accepted or revoked.' });
        return;
      }
      if (resp.status === 410) {
        setState({ phase: 'error', message: 'This invitation has expired.' });
        return;
      }
      if (!resp.ok) {
        setState({ phase: 'error', message: 'Unable to load invitation details. Please try the link again.' });
        return;
      }

      const preview: InvitationPreview = await resp.json();
      setState({ phase: 'preview', preview });
    } catch {
      setState({ phase: 'error', message: 'Unable to reach the server. Check your connection and try again.' });
    }
  }, [token]);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      // Preserve the full invitation URL through login
      const returnTo = `${window.location.pathname}${window.location.search}`;
      window.location.href = buildLoginUrl(returnTo);
      return;
    }

    fetchPreview();
  }, [authLoading, isAuthenticated, fetchPreview]);

  const handleAccept = useCallback(async () => {
    setState({ phase: 'accepting' });

    try {
      const resp = await fetch(
        `${config.gatewayUrl}/v1/invitations/accept?token=${encodeURIComponent(token)}`,
        { credentials: 'include' },
      );

      if (resp.status === 404) {
        setState({ phase: 'error', message: 'Invitation not found. It may have already been accepted or revoked.' });
        return;
      }
      if (resp.status === 410) {
        setState({ phase: 'error', message: 'This invitation has expired.' });
        return;
      }
      if (resp.status === 409) {
        setState({ phase: 'error', message: 'You are already a member of this organization.' });
        return;
      }
      if (!resp.ok) {
        setState({ phase: 'error', message: 'Failed to accept the invitation. Please try again.' });
        return;
      }

      const data = await resp.json();

      // Gateway returns AcceptSuccess or AcceptRedirect
      if (data.redirect_to) {
        // Not authenticated (shouldn't reach here, but handle gracefully)
        window.location.href = buildLoginUrl(
          `${window.location.pathname}${window.location.search}`,
        );
        return;
      }

      const orgName = (state as { phase: string; preview?: InvitationPreview }).preview?.org_name ?? data.org_id;
      const role: string = data.role ?? 'member';

      // Refresh auth context so new org appears
      await refresh();

      setState({ phase: 'success', orgName, role });
      // Navigate to apps hub with welcome banner after short delay for success UI
      setTimeout(() => navigate('/apps?welcome=1', { replace: true }), 1800);
    } catch {
      setState({ phase: 'error', message: 'Unable to reach the server. Check your connection and try again.' });
    }
  }, [token, state, refresh]);

  if (authLoading || state.phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading invitation..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">You've been invited</h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {state.phase === 'preview' && (
            <>
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-1">You're invited to join</p>
                <p className="text-xl font-semibold text-gray-900">{state.preview.org_name}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Role: <span className="font-medium text-gray-700 capitalize">{state.preview.target_role}</span>
                </p>
                {state.preview.email && (
                  <p className="text-sm text-gray-500 mt-1">
                    Invitation sent to <span className="font-medium text-gray-700">{state.preview.email}</span>
                  </p>
                )}
              </div>

              <button
                onClick={handleAccept}
                className="w-full bg-teal-600 text-white py-2.5 px-4 rounded-md font-medium hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors"
              >
                Accept invitation
              </button>

              <button
                onClick={() => navigate('/settings')}
                className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Decline
              </button>
            </>
          )}

          {state.phase === 'accepting' && (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner text="Accepting invitation..." />
            </div>
          )}

          {state.phase === 'success' && (
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">You've joined {state.orgName}</h2>
              <p className="text-sm text-gray-500 mb-6 capitalize">Your role: {state.role}</p>
              <button
                onClick={() => navigate('/settings')}
                className="w-full bg-teal-600 text-white py-2.5 px-4 rounded-md font-medium hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors"
              >
                Go to settings
              </button>
            </div>
          )}

          {state.phase === 'error' && (
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-sm text-gray-700 mb-6">{state.message}</p>
              <button
                onClick={() => navigate('/settings')}
                className="w-full bg-gray-100 text-gray-700 py-2.5 px-4 rounded-md font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
              >
                Go to settings
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
